import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './SocialLinks.styled';
import { FacebookShare, InstagramShare, XShare, EmailShare, SmartphoneShare } from '../../svgs/social_share';
import { useTimeout } from '../../hooks/useTimeout';
import { StyledContentContainer } from '../../containers/StyledContentContainer.styled';
import { SocialVideoDownload } from './components/SocialVideoDownload';
import { CheckCircleFilled, LinkedinShare } from '../../svgs';
import { setColorWithOpacityCss } from '../../utils/setColorWithOpacityCss';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const DEFAULT_APPEAL_SET_CONFIGURATION = {
  copy_link: {
    enabled: true
  },
  email: {
    enabled: true
  },
  facebook: {
    enabled: true
  },
  linkedin: {
    enabled: false
  },
  text_message: {
    enabled: true
  },
  twitter: {
    enabled: true
  },
  video_download: {
    enabled: false
  }
};

/**
 * These props are passed directly to SocialVideoDownload
 * which handles the video preview and download functionality.
 * We don't need to duplicate styling props in our interface since
 * we're already passing the styling props from SocialLinks.
 *
 * Note: The download button is always included in the video preview
 * since it's a core part of the functionality.
 */

const socialLinksDataIds = {
  copyLink: 'cp-social-links-copy-link',
  email: 'cp-social-links-email',
  facebook: 'cp-social-links-facebook',
  instagram: 'cp-social-links-instagram',
  linkedin: 'cp-social-links-linkedin',
  text: 'cp-social-links-sms',
  twitter: 'cp-social-links-twitter',
  videoDownload: 'cp-social-links-video-download'
};

/**
 * Default video preview options
 *
 * FIXME: when support for saving preview options to the campaign data model is implemented,
 *        remove this default, as it can mask when apps and Storybook forget to set values.
 *        Do not use it anywhere aside from the SocialLinks constructor.
 */
const DEFAULT_VIDEO_PREVIEW_OPTIONS = {
  enabled: false,
  title: 'Boost impact with video sharing!',
  description: 'Download our campaign video and share on social platforms.'
};

/**
 * The `SocialLinks` content block presents a list of buttons for sharing a campaign on social media
 * platforms. It can also display a video preview when a social video is available and the
 * showVideoPreview prop is enabled.
 */
export const SocialLinks = ({
  alignXPreset,
  appealSet = DEFAULT_APPEAL_SET_CONFIGURATION,
  cssColor,
  cssBackgroundColorOpacity,
  cssBackgroundColor,
  cssBorderColor,
  cssBorderWidth,
  cssBorderRadius,
  dataIds = socialLinksDataIds,
  isInstagramSharingEnabled = false,
  isMobileOrTablet = false,
  openLinksInNewTab = true,
  parentLayoutInfo,
  socialShareLinks,
  videoPreview = DEFAULT_VIDEO_PREVIEW_OPTIONS,
  ...rest
}) => {
  const [linkIsCopied, setLinkIsCopied] = useState(false);
  const {
    t
  } = useTranslation('blocks.socialLinks');
  const onCopyLink = useCallback(() => {
    if (typeof navigator !== undefined && socialShareLinks?.copy) {
      setLinkIsCopied(true);
      navigator.clipboard.writeText(socialShareLinks?.copy);
    }
  }, [socialShareLinks?.copy]);
  useTimeout(() => {
    setLinkIsCopied(false);
  }, linkIsCopied ? 7000 : null);
  return /*#__PURE__*/_jsxs(StyledContentContainer, {
    parentLayoutInfo: parentLayoutInfo,
    children: [/*#__PURE__*/_jsxs(Styled.SocialLinksContainer, {
      alignXPreset: alignXPreset,
      "data-id": "cp-social-links",
      ...rest,
      children: [appealSet?.copy_link?.enabled && /*#__PURE__*/_jsxs(Styled.CopyLinkContainer, {
        children: [/*#__PURE__*/_jsx(Styled.CopyLinkButton, {
          cssColor: cssColor,
          cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
          cssBorderWidth: cssBorderWidth,
          cssBorderColor: cssBorderColor,
          cssBorderRadius: cssBorderRadius,
          onClick: onCopyLink,
          onKeyDown: e => (e.key === 'Enter' || e.key === ' ') && onCopyLink(),
          "data-id": dataIds.copyLink,
          role: "button",
          tabIndex: 0,
          children: linkIsCopied ? /*#__PURE__*/_jsxs(Styled.CopyLinkButtonText, {
            "aria-label": t('SocialLinks.copyLinkAriaStatus', 'Campaign link status'),
            "aria-live": "assertive",
            children: [t('SocialLinks.linkCopied', 'Copied'), /*#__PURE__*/_jsx(CheckCircleFilled, {})]
          }) : /*#__PURE__*/_jsx(Styled.CopyLinkButtonText, {
            children: t('SocialLinks.copyLink', 'Copy link')
          })
        }), /*#__PURE__*/_jsx(Styled.CopyLinkInputContainer, {
          onClick: onCopyLink,
          children: /*#__PURE__*/_jsx(Styled.CopyLinkInput, {
            cssBorderRadius: cssBorderRadius,
            cssBorderWidth: cssBorderWidth,
            cssBorderColor: cssBorderColor,
            id: "copy-link",
            "aria-label": t('SocialLinks.copyCampaignLinkAriaLabel', 'Copy campaign link'),
            defaultValue: socialShareLinks?.copy,
            readOnly: true
          })
        })]
      }), /*#__PURE__*/_jsx(Styled.ShareOn, {
        children: t('SocialLinks.shareOn', 'Share on')
      }), /*#__PURE__*/_jsxs(Styled.SocialLinkButtonsContainer, {
        alignXPreset: alignXPreset,
        children: [appealSet?.facebook?.enabled && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
          href: socialShareLinks?.facebook,
          "data-id": dataIds.facebook,
          target: openLinksInNewTab ? '_blank' : undefined,
          children: [/*#__PURE__*/_jsx(FacebookShare, {}), " ", /*#__PURE__*/_jsx("span", {
            children: t('SocialLinks.facebook', 'Facebook')
          })]
        }), appealSet?.linkedin?.enabled && false && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
          href: socialShareLinks?.linkedin,
          "data-id": dataIds.linkedin,
          target: openLinksInNewTab ? '_blank' : undefined,
          children: [/*#__PURE__*/_jsx(LinkedinShare, {}), " ", /*#__PURE__*/_jsx("span", {
            children: t('SocialLinks.linkedin', 'LinkedIn')
          })]
        }), appealSet?.twitter?.enabled && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
          href: socialShareLinks?.twitter,
          "data-id": dataIds.twitter,
          target: openLinksInNewTab ? '_blank' : undefined,
          children: [/*#__PURE__*/_jsx(XShare, {}), " ", /*#__PURE__*/_jsx("span", {
            children: t('SocialLinks.post', 'Post')
          })]
        }), isInstagramSharingEnabled && isMobileOrTablet && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
          href: socialShareLinks?.instagram,
          "data-id": dataIds.instagram,
          target: openLinksInNewTab ? '_blank' : undefined,
          children: [/*#__PURE__*/_jsx(InstagramShare, {}), " ", /*#__PURE__*/_jsx("span", {
            children: t('SocialLinks.instagram', 'Instagram')
          })]
        }), appealSet?.text_message?.enabled && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
          href: socialShareLinks?.sms,
          "data-id": dataIds.text,
          children: [/*#__PURE__*/_jsx(SmartphoneShare, {}), " ", /*#__PURE__*/_jsx("span", {
            children: t('SocialLinks.text', 'Text')
          })]
        }), appealSet?.email?.enabled && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
          href: socialShareLinks?.email,
          "data-id": dataIds.email,
          target: "_blank",
          children: [/*#__PURE__*/_jsx(EmailShare, {}), " ", /*#__PURE__*/_jsx("span", {
            children: t('SocialLinks.email', 'Email')
          })]
        })]
      })]
    }), appealSet?.video_download?.enabled && socialShareLinks?.video && /*#__PURE__*/_jsx(SocialVideoDownload, {
      videoUrl: socialShareLinks.video,
      showPreview: videoPreview.enabled,
      previewTitle: videoPreview.title,
      previewDescription: videoPreview.description,
      showDownloadInPreview: videoPreview.enabled,
      downloadButtonLabel: t('SocialLinks.videoDownload', 'Download video'),
      cssBackgroundColor: cssBackgroundColor,
      cssBackgroundColorOpacity: cssBackgroundColorOpacity,
      cssBorderRadius: cssBorderRadius,
      cssBorderColor: cssBorderColor,
      cssBorderWidth: cssBorderWidth,
      cssColor: cssColor,
      alignXPreset: alignXPreset
    })]
  });
};